import { Injectable } from '@angular/core';
import { ServerDataService } from '@core/services/server-data/server-data.service';
import { ShareService } from '../share-data/share-data.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { ScreenshotService } from '@core/services/screenshot/screenshot.service';
import { SetupPageService } from '@core/services/setup-page/setup-page.service';
import * as _ from 'lodash';
import { CLASSES, DEVICES, SELECTORS, STORAGE_NAMES } from '@root/app.config';
import { IndexedDBService } from '@core/services/indexed-db/indexed-db.service';
import { IVSceneSettings, IBlindSelect, BlindData } from '@root/app.interfaces';
import { Observable } from 'rxjs';
import { VariantService } from '@core/services/variant/variant.service';

@Injectable({
	providedIn: 'root',
})
export class SaveSessionBlindsService {
	isNewBlind: boolean | string;
	ivSceneSettings: IVSceneSettings;
	getBlindSelect = this.shareService.getBlindSelect.subscribe((res: IBlindSelect) => {
		this.isNewBlind = res.status === 'new';
	});

	constructor(
		private setupPageService: SetupPageService,
		private serverDataService: ServerDataService,
		private screenShotService: ScreenshotService,
		private sessionStorageService: SessionStorageService,
		private shareService: ShareService,
		private indexedDBService: IndexedDBService,
		private variant: VariantService,
	) {}

	PutStorageDataToServer(resolve?: (value: boolean) => void, loader?: boolean): void {
		const sessionSaved = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_session_saved);

		if (sessionSaved) {
			const sessionKey = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_session);
			const getStorageData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
			const currentBlindId = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_current_blind_id);
			const lastBlindSetup = getStorageData[getStorageData.length - 1]?.setup;
			const toolBarAccordion = document.querySelector(SELECTORS.tool_bar_accordion);
			const storageDataWithAction = this.getStorageDataWithAction('blinds-update');
			const setIVWrapperLoader = (status: boolean) =>
				document.querySelector(SELECTORS.wrapper)?.classList.toggle(CLASSES.iv_loading, status);

			if (lastBlindSetup?.mounting || _.isEmpty(getStorageData)) {
				if (this.isNewBlind) {
					toolBarAccordion.classList.add(CLASSES.loading);
					this.isNewBlind = false;
				}

				if (resolve) {
					setIVWrapperLoader(loader);
				}

				this.preventReloadPage(true);
				this.screenShotService.saveScreenShot(currentBlindId, true);
				this.serverDataService.PutSessionData(storageDataWithAction, sessionKey).subscribe(() => {
					toolBarAccordion?.classList.remove(CLASSES.loading);
					this.preventReloadPage(false);
					this.shareService.setBlindsUpdated(true);

					if (resolve) {
						setIVWrapperLoader(false);
						resolve(true);
					}
				});
			}
		}
	}

	PutStorageDataToServerPromise(loader = true) {
		return new Promise((resolve) => {
			this.updateIVSceneSettings().then(() => this.PutStorageDataToServer(resolve, loader));
		});
	}

	putSaveBlinds(email = '', resolve?: (value: boolean) => void): void {
		this.saveSessionOnAction('email').subscribe(() => {
			this.sessionStorageService.setBlindData(true, STORAGE_NAMES.zip_blind_progress_saved);
			this.sessionStorageSessionKeyHelper();

			const emailData = {
				email,
				session_key: this.getSessionKeyForSavingHandler(),
				country: this.setupPageService.getCountryCode(),
				is_shop: this.variant.isShop(),
			};

			this.serverDataService.PostEmailData(emailData).subscribe(() => {
				if (resolve) {
					resolve(true);
				}
			});
		});
	}

	putSaveBlindsPromise(email = '') {
		return new Promise((resolve) => {
			this.updateIVSceneSettings().then(() => this.putSaveBlinds(email, resolve));
		});
	}

	putDownloadData(resolve?: (value: void) => void): void {
		this.saveSessionOnAction('pdf').subscribe(() => {
			this.sessionStorageSessionKeyHelper();

			const link =
				this.serverDataService.serverData().main_link +
				this.serverDataService.serverData().link.download +
				'/' +
				this.getSessionKeyForSavingHandler();

			this.serverDataService.getPdfData(link).then(() => resolve());
		});
	}

	putDownloadDataPromise() {
		return new Promise((resolve) => {
			this.updateIVSceneSettings().then(() => this.putDownloadData(resolve));
		});
	}

	saveSessionOnNavigateToQuotation(): Promise<void> {
		return new Promise((resolve) => {
			this.updateIVSceneSettings().then(() => {
				this.saveSessionOnAction('quotation').subscribe(() => {
					this.sessionStorageSessionKeyHelper();
					resolve();
				});
			});
		});
	}

	async updateIVSceneSettings() {
		const ivUploadedBackground = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_uploaded_image_visualisation_background);
		const ivCurrentBackground = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_image_visualisation_background);
		const ivLastType = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_iv_type);
		const ivDevice = this.setupPageService.mobileAndTabletCheck() ? DEVICES.mobile : DEVICES.desktop;
		const openings = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_openings_data);
		const detectionImageSize = this.sessionStorageService.getSession(STORAGE_NAMES.zip_detection_image_size);

		const ivSceneUpload = ivUploadedBackground || undefined;
		const ivSceneSample = ivCurrentBackground?.type ? ivCurrentBackground : undefined;

		if (ivSceneUpload) {
			await this.indexedDBService.getImage(STORAGE_NAMES.zip_image_visualisation_background, ivSceneUpload.id).then((image) => {
				ivSceneUpload.image = image?.base64;
			});
		}

		this.ivSceneSettings = {
			upload: ivSceneUpload,
			sample: ivSceneSample,
			iv_last_type: ivLastType,
			device: ivDevice,
			openings,
			detectionImageSize,
		};
	}

	sessionStorageSessionKeyHelper(): void {
		this.sessionStorageService.setBlindData(this.getSessionKeyForSavingHandler(), STORAGE_NAMES.zip_blind_session);
		this.shareService.setLinkSessionKey(this.getSessionKeyForSavingHandler());

		this.sessionStorageService.setBlindData(true, STORAGE_NAMES.zip_blind_session_saved);
		this.sessionStorageService.removeBlindData(STORAGE_NAMES.zip_blind_temp_session);
	}

	getSessionKeyForSavingHandler(): string {
		const hasSavedSession = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_session_saved);
		const tempSessionKey = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_temp_session);
		const sessionKey = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_session);

		return hasSavedSession ? sessionKey : tempSessionKey;
	}

	preventReloadPage(status: boolean): void {
		window.onbeforeunload = status
			? (e) => {
					e.returnValue = '';
					return status;
				}
			: () => {};
	}

	updateBlindsStorage(blinds: BlindData[]): void {
		this.sessionStorageService.setBlindData(blinds, STORAGE_NAMES.zip_blind_data);
	}

	stripUnconfiguredSetups(storageData: BlindData[]): BlindData[] {
		storageData.forEach((blind) => {
			const setupData = blind.setup ?? {};

			Object.entries(setupData).forEach(([key, section]) => {
				if (!section || typeof section !== 'object') {
					return;
				}

				if (key === 'frames') {
					const frameSections = section as Record<string, { configured?: boolean; is_custom?: boolean }>;

					Object.entries(frameSections).forEach(([frameKey, frameSection]) => {
						if (!frameSection) {
							return;
						}
						// don't delete custom frames even if not configured
						if (!frameSection.configured && Object.hasOwn(frameSection, 'configured')) {
							delete blind.setup.frames[frameKey];
						}
					});
				} else if ('configured' in section && !section.configured) {
					delete blind.setup[key];
				}
			});
		});

		return storageData;
	}

	saveSessionOnAction<T>(action: string, options: { suppressErrorModal?: boolean } = {}): Observable<T> {
		const getStorageData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
		const storageDataWithAction = this.getStorageDataWithAction(action);
		this.updateBlindsStorage(getStorageData);
		return this.serverDataService.PutSessionData(storageDataWithAction, this.getSessionKeyForSavingHandler(), options);
	}

	private getStorageDataWithAction(action: string) {
		const getStorageData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
		const strippedStorageData = this.stripUnconfiguredSetups(structuredClone(getStorageData));
		const embedKey = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_embed_key);
		const sceneIVScreenshot = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_iv_screenshot);
		return {
			action,
			country: this.setupPageService.getCountryCode(),
			embed_key: embedKey,
			blinds: strippedStorageData,
			iv_screenshot: sceneIVScreenshot ? sceneIVScreenshot : null,
			iv_scene_settings: this.ivSceneSettings,
			is_shop: this.variant.isShop(),
		};
	}
}
